import { useQuery } from '@tanstack/react-query';
import { productService } from '@/services/productService';
import { logError } from '@/utils/errorHandler';

interface TagOption {
  label: string;
  value: string;
}

interface UseFetchTagsOptions {
  staleTime?: number; // How long data is considered fresh (default: 30 minutes)
  gcTime?: number; // How long data stays in cache (default: 1 hour)
  enabled?: boolean; // Whether the query should run
  searchTerm?: string; // Optional search term for filtering tags
}

/**
 * Custom hook to fetch and cache product tags with configurable cache settings
 */
export function useFetchTags(options: UseFetchTagsOptions = {}) {
  const {
    staleTime = 30 * 60 * 1000, // 30 minutes - tags don't change often
    gcTime = 60 * 60 * 1000, // 1 hour cache time
    enabled = true,
    searchTerm = '',
  } = options;

  return useQuery({
    queryKey: ['product-tags', searchTerm],
    queryFn: async (): Promise<TagOption[]> => {
      try {
        const tags = await productService.searchTags(searchTerm);
        return tags;
      } catch (error) {
        logError(error instanceof Error ? error : new Error(String(error)), {
          component: 'useFetchTags',
          action: 'queryFn',
          metadata: {
            searchTerm,
            context: 'tag_search_api_failure',
          },
        });
        return [];
      }
    },
    enabled,
    staleTime, // Data considered fresh for 30 minutes
    gcTime, // Keep in cache for 1 hour
    refetchOnWindowFocus: false, // Don't refetch when window regains focus
    retry: (failureCount, error) => {
      // Don't retry on 4xx errors (client errors)
      if (error && typeof error === 'object' && 'status' in error) {
        const status = (error as any).status;
        if (status >= 400 && status < 500) {
          return false;
        }
      }
      // Retry up to 2 times for other errors
      return failureCount < 2;
    },
  });
}

/**
 * Hook specifically for getting all tags (no search term)
 * This version has even longer cache since all tags change infrequently
 */
export function useFetchAllTags(options: Omit<UseFetchTagsOptions, 'searchTerm'> = {}) {
  return useFetchTags({
    ...options,
    staleTime: options.staleTime ?? 60 * 60 * 1000, // 1 hour for all tags
    gcTime: options.gcTime ?? 2 * 60 * 60 * 1000, // 2 hours cache time
    searchTerm: '',
  });
}
